/**
 * Redux Slice pour le DocReader — Application de lecture Anagogia
 *
 * Gère l'état UI du lecteur de documents :
 * - Fichier ouvert, zoom, navigation
 * - Sélection de texte
 * - Panneaux (recherche, document virtuel), marge des entrées
 * - Surlignage bidirectionnel (hoveredSourceLine, lu par ModalNote)
 *
 * Ce slice ne porte aucun état asynchrone : les notes et entrées du document
 * sont dérivées de state.notes.data par les hooks useDocumentEntries /
 * useVirtualDocument, et le chargement des fichiers est géré par les renderers.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import type {
  DocReaderState,
  DocReaderFile,
  TextSelection,
  PassageTarget,
  PassageLine,
  PendingReplaceTarget,
} from '../../components/DocReader/types';

// ─── État initial ───────────────────────────────────────────────────────────

const initialState: DocReaderState = {
  currentFile: null,
  isOpen: false,
  viewMode: 'modal',
  zoom: 100,
  currentPage: 1,
  totalPages: 0,
  selection: null,
  entriesPanelOpen: false,
  virtualDocOpen: false,
  highlightedSectionId: null,
  entriesMarginVisible: false,
  isFullscreen: false,
  hoveredSourceLine: null,
  searchHighlight: null,
  passageHighlight: null,
  pendingPassage: null,
  pendingReplace: null,
};

// ─── Slice ──────────────────────────────────────────────────────────────────

const docReaderSlice = createSlice({
  name: 'docReader',
  initialState,
  reducers: {
    /** Ouvrir le lecteur avec un fichier, éventuellement sur un passage (C16) */
    openDocReader(state, action: PayloadAction<{ file: DocReaderFile; viewMode?: 'modal' | 'fullpage'; passage?: PassageTarget | null; replace?: PendingReplaceTarget | null }>) {
      state.currentFile = action.payload.file;
      state.isOpen = true;
      state.viewMode = action.payload.viewMode || 'modal';
      state.zoom = 100;
      state.currentPage = 1;
      state.totalPages = 0;
      state.selection = null;
      state.pendingPassage = action.payload.passage ?? null;
      state.pendingReplace = action.payload.replace ?? null;
    },

    /** Note dont le passage est à remplacer (06/10/2026) — dans le document déjà ouvert, ou avec `openDocReader({ replace })` */
    setPendingReplace(state, action: PayloadAction<PendingReplaceTarget | null>) {
      state.pendingReplace = action.payload;
    },

    /** Étape « modifier le passage » prise en charge par DocReaderPage */
    clearPendingReplace(state) {
      state.pendingReplace = null;
    },

    /** Passage à atteindre dans le document déjà ouvert (liaison note → passage, C16) */
    setPendingPassage(state, action: PayloadAction<PassageTarget | null>) {
      state.pendingPassage = action.payload;
    },

    /** Passage consommé par DocReaderPage */
    clearPendingPassage(state) {
      state.pendingPassage = null;
    },

    /** Fermer le lecteur */
    closeDocReader(state) {
      state.isOpen = false;
      state.currentFile = null;
      state.selection = null;
      state.entriesPanelOpen = false;
      state.virtualDocOpen = false;
      state.highlightedSectionId = null;
      state.entriesMarginVisible = false;
      state.isFullscreen = false;
      state.searchHighlight = null;
      state.passageHighlight = null;
      state.pendingPassage = null;
      state.pendingReplace = null;
    },

    /** Mettre à jour le zoom */
    setZoom(state, action: PayloadAction<number>) {
      state.zoom = Math.max(50, Math.min(200, action.payload));
    },

    /** Zoom in (+20%) */
    zoomIn(state) {
      state.zoom = Math.min(200, state.zoom + 20);
    },

    /** Zoom out (-20%) */
    zoomOut(state) {
      state.zoom = Math.max(50, state.zoom - 20);
    },

    /** Mettre à jour la pagination (remontée par les renderers via onPageChange) */
    setPageInfo(state, action: PayloadAction<{ current: number; total: number }>) {
      state.currentPage = action.payload.current;
      state.totalPages = action.payload.total;
    },

    /** Mise à jour de la sélection de texte */
    setTextSelection(state, action: PayloadAction<TextSelection | null>) {
      state.selection = action.payload;
    },

    /** Toggle du panel latéral de recherche */
    toggleEntriesPanel(state) {
      state.entriesPanelOpen = !state.entriesPanelOpen;
    },

    /** Ouvrir/fermer le panel de recherche */
    setEntriesPanelOpen(state, action: PayloadAction<boolean>) {
      state.entriesPanelOpen = action.payload;
    },

    /** Toggle du panel Document Virtuel */
    toggleVirtualDoc(state) {
      state.virtualDocOpen = !state.virtualDocOpen;
    },

    /** Ouvrir/fermer le panel Document Virtuel */
    setVirtualDocOpen(state, action: PayloadAction<boolean>) {
      state.virtualDocOpen = action.payload;
    },

    /** Mettre en surbrillance une section (cross-panel navigation) */
    setHighlightedSection(state, action: PayloadAction<string | null>) {
      state.highlightedSectionId = action.payload;
    },

    /** Toggle de la marge des entrées */
    toggleEntriesMargin(state) {
      state.entriesMarginVisible = !state.entriesMarginVisible;
    },

    /** Définir la visibilité de la marge des entrées */
    setEntriesMarginVisible(state, action: PayloadAction<boolean>) {
      state.entriesMarginVisible = action.payload;
    },

    /** Plein écran */
    setFullscreen(state, action: PayloadAction<boolean>) {
      state.isFullscreen = action.payload;
    },

    /** Changer le mode d'affichage */
    setViewMode(state, action: PayloadAction<'modal' | 'fullpage'>) {
      state.viewMode = action.payload;
    },

    /** Ligne source survolée (surlignage bidirectionnel editeur <-> PDF) */
    setHoveredSourceLine(state, action: PayloadAction<{ noteId: string; page: number; lineNumber: number } | null>) {
      state.hoveredSourceLine = action.payload;
    },

    /** Résultat de recherche texte à surligner temporairement dans le PDF */
    setSearchHighlight(state, action: PayloadAction<{ page: number; yPercent: number } | null>) {
      state.searchHighlight = action.payload;
    },

    /** Passage cité à surligner ligne à ligne dans le PDF (C16), `null` pour effacer */
    setPassageHighlight(state, action: PayloadAction<PassageLine[] | null>) {
      state.passageHighlight = action.payload && action.payload.length > 0 ? action.payload : null;
    },
  },
});

// ─── Actions ────────────────────────────────────────────────────────────────

export const {
  openDocReader,
  closeDocReader,
  setZoom,
  zoomIn,
  zoomOut,
  setPageInfo,
  setTextSelection,
  toggleEntriesPanel,
  setEntriesPanelOpen,
  toggleEntriesMargin,
  setEntriesMarginVisible,
  setFullscreen,
  setViewMode,
  toggleVirtualDoc,
  setVirtualDocOpen,
  setHighlightedSection,
  setHoveredSourceLine,
  setSearchHighlight,
  setPassageHighlight,
  setPendingPassage,
  clearPendingPassage,
  setPendingReplace,
  clearPendingReplace,
} = docReaderSlice.actions;

// ─── Sélecteurs ─────────────────────────────────────────────────────────────

export const selectDocReaderFile = (state: RootState) => state.docReader?.currentFile;
export const selectDocReaderIsOpen = (state: RootState) => state.docReader?.isOpen;
export const selectDocReaderViewMode = (state: RootState) => state.docReader?.viewMode;
export const selectDocReaderZoom = (state: RootState) => state.docReader?.zoom;
export const selectDocReaderCurrentPage = (state: RootState) => state.docReader?.currentPage;
export const selectDocReaderTotalPages = (state: RootState) => state.docReader?.totalPages;
export const selectDocReaderSelection = (state: RootState) => state.docReader?.selection;
export const selectEntriesMarginVisible = (state: RootState) => state.docReader?.entriesMarginVisible;
export const selectDocReaderEntriesPanel = (state: RootState) => state.docReader?.entriesPanelOpen;
export const selectDocReaderVirtualDocOpen = (state: RootState) => state.docReader?.virtualDocOpen;
export const selectDocReaderIsFullscreen = (state: RootState) => state.docReader?.isFullscreen;
export const selectHighlightedSectionId = (state: RootState) => state.docReader?.highlightedSectionId;
export const selectHoveredSourceLine = (state: RootState) => state.docReader?.hoveredSourceLine;
export const selectSearchHighlight = (state: RootState) => state.docReader?.searchHighlight;
export const selectPassageHighlight = (state: RootState) => state.docReader?.passageHighlight;
export const selectPendingPassage = (state: RootState) => state.docReader?.pendingPassage;
export const selectPendingReplace = (state: RootState) => state.docReader?.pendingReplace;

export default docReaderSlice.reducer;
